// Discovered channels shown under the vendor device they belong to (a SUPLA device, an Exalus module, a Lavva
// meter box), devices grouped by category. Pure, so the grouping rules are testable.

export interface ChannelLike {
  sourceDeviceId: string;
  name: string;
  category: string;
  parent: { id: string; name: string; model: string | null } | null;
}

export interface SourceDevice<T extends ChannelLike> {
  id: string;
  name: string;
  model: string | null;
  /** The most significant category of its channels: a module with a relay and a meter is energy. */
  category: string;
  channels: T[];
}

export function groupBySource<T extends ChannelLike>(
  channels: readonly T[],
  rank: (category: string) => number,
): { category: string; devices: SourceDevice<T>[] }[] {
  const devices = new Map<string, SourceDevice<T>>();
  for (const c of channels) {
    // A channel whose integration names no device stands for itself.
    const id = c.parent?.id ?? `channel:${c.sourceDeviceId}`;
    const device = devices.get(id) ?? {
      id,
      name: c.parent?.name ?? c.name,
      model: c.parent?.model ?? null,
      category: c.category,
      channels: [],
    };
    device.channels.push(c);
    if (rank(c.category) < rank(device.category)) {
      device.category = c.category;
    }
    devices.set(id, device);
  }
  const byCategory = new Map<string, SourceDevice<T>[]>();
  for (const d of devices.values()) {
    d.channels.sort((a, b) => a.name.localeCompare(b.name));
    byCategory.set(d.category, [...(byCategory.get(d.category) ?? []), d]);
  }
  return [...byCategory.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([category, list]) => ({
      category,
      devices: list.sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

/** The vendor's own identifier, without the platform's kind prefix (`mac:`, `device:`). */
export function vendorId(id: string): string {
  return id.replace(/^(mac|device|channel):/, "");
}
